import React, {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';


import {removePill, replacePill, setPill} from 'src/components/criteria-strip';
import {USER_STRIP_LABELS} from 'src/components/criteria-strip/constants';
import {
    selectAccountManager,
    selectSearchCriteria,
    setAccountManager,
} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {FormInput} from 'src/components/input';
import {QueryArea} from 'src/components/query-area';
import {WidthItem} from 'src/components/width-item';
import {MAX_NUMBER_VALUE, MAX_SELECTED_ACCOUNTS, SEARCH_RESULT_TABS} from 'src/constants';
import {useUserStatuses, useUserTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';

import ComboBox, {ComboboxItem} from '@pb/kit-component-combo-box';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';

import {AccountTeamMemberSuggest} from '../../components/account-manager-suggest';
import {validateEmails, validateName, validatePbRtsId, validateUserIds} from '../../utils';
import {SearchCriteriaTabs} from '../search-criteria-tabs';

import type {AccountManagerSearchResult} from 'src/openapi/admin/models/AccountManagerSearchResult';
import type {Pill} from 'src/typedef';

const INPUT_WIDTH = 400;

export const UserSearch = () => {
    const criteria = useSelector(selectSearchCriteria);

    const dispatch = useDispatch();
    const {userStatuses} = useUserStatuses();
    const {userTypes} = useUserTypes();

    const {searchCriteriaUser} = useEventTracking();
    const selectedManager = useSelector(selectAccountManager);

    const setFirstName = useCallback((firstName: string) => {
        if ((criteria?.firstName?.value ?? '') !== firstName) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.firstName});
        }

        if (firstName) {
            dispatch(setPill({
                propName: 'firstName',
                pill: {value: firstName, invalid: !validateName(firstName)},
            }));
        } else {
            dispatch(removePill({propName: 'firstName'}));
        }
    }, [criteria, searchCriteriaUser, dispatch]);

    const setLastName = useCallback((lastName: string) => {
        if ((criteria?.lastName?.value ?? '') !== lastName) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.lastName});
        }

        if (lastName) {
            dispatch(setPill({
                propName: 'lastName',
                pill: {value: lastName, invalid: !validateName(lastName)}}));
        } else {
            dispatch(removePill({propName: 'lastName'}));
        }
    }, [criteria, dispatch, searchCriteriaUser]);

    const onSetUserIds = useCallback((userIds: Pill<string>[]) => {
        if (userIds.length) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.userIds});
            dispatch(setPill({propName: 'userIds', pill: userIds}));
        }
    }, [dispatch, searchCriteriaUser]);

    const onRemoveUserIds = useCallback((userId: string) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.userIds});
        dispatch(removePill({propName: 'userIds', forRemove: userId}));
    }, [dispatch, searchCriteriaUser]);

    const onSetUserEmails = useCallback((userEmails: Pill<string>[]) => {
        if (userEmails.length) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.userEmails});
            dispatch(setPill({propName: 'userEmails', pill: userEmails}));
        }
    }, [dispatch, searchCriteriaUser]);

    const onRemoveUserEmails = useCallback((userEmail: string) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.userEmails});
        dispatch(removePill({propName: 'userEmails', forRemove: userEmail}));
    }, [dispatch, searchCriteriaUser]);

    const onUserStatusesChange = useCallback((statuses: string[]) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.userStatuses});
        if (statuses.length) {
            dispatch(replacePill({propName: 'userStatuses', pill: statuses.map(value => ({value}))}));
        } else {
            dispatch(removePill({propName: 'userStatuses'}));
        }
    }, [dispatch, searchCriteriaUser]);

    const onSetUserTypes = useCallback((types: string[]) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.userTypes});
        if (types.length) {
            dispatch(replacePill({propName: 'userTypes', pill: types.map(value => ({value}))}));
        } else {
            dispatch(removePill({propName: 'userTypes'}));
        }
    }, [dispatch, searchCriteriaUser]);

    const onSetAccountsIds = useCallback((accountIds: Pill<string>[]) => {
        if (accountIds.length) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.accountIds});
            dispatch(setPill({propName: 'accountIds', pill: accountIds}));
        }
    }, [dispatch, searchCriteriaUser]);

    const onRemoveAccountsId = useCallback((accountId: string) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.accountIds});
        dispatch(removePill({propName: 'accountIds', forRemove: accountId}));
    }, [dispatch, searchCriteriaUser]);

    const onSetAccountManager = useCallback((manager: AccountManagerSearchResult | undefined) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.accountManagerId});
        if (manager) {
            dispatch(setAccountManager(manager));
            dispatch(setPill({propName: 'accountManagerId', pill: {value: manager.id}}));
        } else {
            dispatch(removePill({propName: 'accountManagerId'}));
        }
    }, [dispatch, searchCriteriaUser]);

    const setPbRtsId = useCallback((rtsId: string) => {
        if ((criteria?.rtsId?.value ?? '') !== rtsId) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.rtsId});
        }

        if (rtsId) {
            dispatch(setPill({
                propName: 'rtsId',
                pill: {value: rtsId, invalid: !validatePbRtsId(rtsId)},
            }));
        } else {
            dispatch(removePill({propName: 'rtsId'}));
        }
    }, [criteria, dispatch, searchCriteriaUser]);

    const onSetPbSalesforceIds = useCallback((pbSalesforceIds: Pill<string>[]) => {
        if (pbSalesforceIds.length) {
            searchCriteriaUser({parameterName: USER_STRIP_LABELS.pbSalesforceIds});
            dispatch(setPill({propName: 'pbSalesforceIds', pill: pbSalesforceIds}));
        }
    }, [dispatch, searchCriteriaUser]);


    const onRemovePbSalesforceId = useCallback((pbSalesforceId: string) => {
        searchCriteriaUser({parameterName: USER_STRIP_LABELS.pbSalesforceIds});
        dispatch(removePill({propName: 'pbSalesforceIds', forRemove: pbSalesforceId}));
    }, [dispatch, searchCriteriaUser]);

    if (!criteria) {
        return null;
    }

    return (
        <Group direction="v" gap="xl">
            <SearchCriteriaTabs activeTab={SEARCH_RESULT_TABS.USERS}/>
            <FormSection borderBottom={true} headline="User Criteria">
                <Group alignItems="start" gap="xxl">
                    <Group direction="v">
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.userEmails}</Label>
                            <QueryArea
                                placeholder="Enter email addresses"
                                items={criteria.userEmails || []}
                                onValidate={validateEmails}
                                onAdd={onSetUserEmails}
                                customSeparator={/[\s,]+/}
                                onRemove={onRemoveUserEmails}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </WidthItem>
                        <FormInput
                            maxLength={50}
                            width={INPUT_WIDTH}
                            label={USER_STRIP_LABELS.firstName}
                            valid={criteria?.firstName?.invalid ? !criteria?.firstName?.invalid : undefined}
                            value={criteria?.firstName?.value || ''}
                            placeholder="Enter first name"
                            onBlur={setFirstName}
                        />
                        <FormInput
                            maxLength={50}
                            width={INPUT_WIDTH}
                            label={USER_STRIP_LABELS.lastName}
                            valid={criteria?.lastName?.invalid ? !criteria?.lastName?.invalid : undefined}
                            placeholder="Enter last name"
                            value={criteria?.lastName?.value || ''}
                            onBlur={setLastName}
                        />
                        <AccountTeamMemberSuggest
                            width={INPUT_WIDTH}
                            placeholder="Select team member"
                            label={USER_STRIP_LABELS.accountManagerId}
                            selectedTeamMember={selectedManager || undefined}
                            onSelect={onSetAccountManager}
                        />
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.accountIds}</Label>
                            <QueryArea
                                placeholder="Enter account IDs"
                                items={criteria.accountIds || []}
                                onValidate={(id: string) => !isNaN(+id) && +id > 0 && +id < MAX_NUMBER_VALUE}
                                onAdd={onSetAccountsIds}
                                onRemove={onRemoveAccountsId}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </WidthItem>
                    </Group>
                    <Group direction="v">
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.userIds}</Label>
                            <QueryArea
                                placeholder="Enter user IDs"
                                items={criteria.userIds || []}
                                onValidate={validateUserIds}
                                onAdd={onSetUserIds}
                                onRemove={onRemoveUserIds}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </WidthItem>
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.userTypes}</Label>
                            <ComboBox
                                multiselectable={true}
                                placeholder="Select user types"
                                value={criteria.userTypes?.map(({value}) => value) || []}
                                onChange={onSetUserTypes}>
                                {userTypes.map(({code, description}) => (
                                    <ComboboxItem key={code} value={code} caption={description}/>
                                ))}
                            </ComboBox>
                        </WidthItem>
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.userStatuses}</Label>
                            <ComboBox
                                placeholder="Select user statuses"
                                multiselectable={true}
                                value={criteria.userStatuses?.map(({value}) => value) || []}
                                onChange={onUserStatusesChange}>
                                {userStatuses.map(({code, description}) => (
                                    <ComboboxItem key={code} value={code} caption={description}/>
                                ))}
                            </ComboBox>
                        </WidthItem>
                        <FormInput
                            width={INPUT_WIDTH}
                            label={USER_STRIP_LABELS.rtsId}
                            valid={criteria?.rtsId?.invalid ? !criteria?.rtsId?.invalid : undefined}
                            maxLength={12}
                            placeholder="PB RTS ID"
                            value={criteria?.rtsId?.value || ''}
                            onBlur={setPbRtsId}
                        />
                        <WidthItem width={INPUT_WIDTH}>
                            <Label>{USER_STRIP_LABELS.pbSalesforceIds}</Label>
                            <QueryArea
                                placeholder="Enter PB Salesforce IDs"
                                items={criteria.pbSalesforceIds || []}
                                onValidate={(id: string) => validatePbRtsId(id)}
                                onAdd={onSetPbSalesforceIds}
                                onRemove={onRemovePbSalesforceId}
                                maxSelected={MAX_SELECTED_ACCOUNTS}
                            />
                        </WidthItem>
                    </Group>
                </Group>
            </FormSection>
        </Group>
    );
};
